/**
 * SettingsPageHeader: the header every Settings section renders, and every
 * staff team-configuration page. The title and description come from the
 * page's registry entry, so each exists in exactly one place; a page adds
 * at most one badge beside the title and one action at the right.
 *
 * Under staff impersonation a mono eyebrow says the team's data is read
 * only. A user-scoped section (Teams) shows the staff member's own data
 * (sections.ts), so it gets no eyebrow. A team-configuration page
 * (components/internal/team-config) has no scope at all: entering that
 * area ends impersonation (TeamConfigLayout), so the page never renders
 * under it and there is nothing to say.
 */

import { PageHeader } from '@/components/ui/page-header'
import { useTeam } from '@/contexts/TeamContext'
import { cn } from '@/lib/utils'

import { type SettingsScope } from './sections'
import { EYEBROW } from '@/components/ui/eyebrow'

/** What the header reads from a registry entry. */
interface SectionHeading {
  label: string
  description?: string
  /** Whose data the page shows; a staff team-configuration page has none. */
  scope?: SettingsScope
}

interface SettingsPageHeaderProps {
  section: SectionHeading
  badge?: React.ReactNode
  action?: React.ReactNode
  /** Over the default spacing, e.g. a smaller bottom gap on a page with no description. */
  className?: string
}

export function SettingsPageHeader({
  section,
  badge,
  action,
  className,
}: SettingsPageHeaderProps): React.JSX.Element {
  const { impersonatingTeamId } = useTeam()
  const readOnly = impersonatingTeamId !== null && section.scope === 'team'
  return (
    <div className={cn('space-y-2 pb-6', className)}>
      <PageHeader
        title={section.label}
        description={section.description}
        badge={badge}
        actions={action}
      />
      {readOnly && (
        <p className={EYEBROW}>You are viewing this team as staff, so nothing here can be changed.</p>
      )}
    </div>
  )
}
